import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import ConsultationButton from "./components/ConsultationButton";

// ✅ Lazy load non-critical components
const Home = lazy(() => import("./pages/Home"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const ContactMessages = lazy(() => import("./pages/ContactMessages"));
const Tools = lazy(() => import("./pages/Tools"));
const Team = lazy(() => import("./pages/Team"));
const Services = lazy(() => import("./pages/Services"));
const IPRService = lazy(() => import("./pages/IPRService"));
const StartupServices = lazy(() => import("./pages/StartupServices"));
const CorporateNCLT = lazy(() => import("./pages/CorporateNCLT"));
const Litigation = lazy(() => import("./pages/Litigation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Chatbot = lazy(() => import("./components/Chatbot"));

// ✅ Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-12 h-12 border-4 border-[#B8860B] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// ✅ Initialize Google Analytics 4
const GA_MEASUREMENT_ID = "G-TBJ835RLPJ";
ReactGA.initialize(GA_MEASUREMENT_ID, {
  gtagOptions: {
    send_page_view: false,
  },
});

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <ScrollToSection />
      <PageTracker />

      <Navbar />
      <WhatsAppButton />
      <ConsultationButton />

      {/* ✅ Lazy loaded chatbot */}
      <Suspense fallback={null}>
        <Chatbot />
      </Suspense>

      <div className="flex flex-col min-h-screen overflow-x-hidden">
        <main className="flex-grow">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/blog/:id" element={<BlogDetail />} />
              <Route path="/contacts" element={<ContactMessages />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/team" element={<Team />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/ipr" element={<IPRService />} />
              <Route path="/services/startup" element={<StartupServices />} />
              <Route path="/services/corporate" element={<CorporateNCLT />} />
              <Route path="/services/litigation" element={<Litigation />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

// ✅ Page Tracker Component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
      title: document.title,
    });
  }, [location]);

  return null;
}

// Scroll to top
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  return null;
}

// Handle hash links
function ScrollToSection() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const navbar = document.querySelector('nav');
          const navbarHeight = navbar ? navbar.offsetHeight : 75;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 150);
    }
  }, [location]);

  return null;
}

export default App;